/**
 * 설비 카탈로그 API 클라이언트 — /api/equipment-catalog-handler?resource=…
 * 서버: api/equipment-catalog-handler.js · 규칙: lib/equipment-catalog.js
 */
import type { StoredDefinition } from "../../../features/digitalTwin/equipment/definition/types";
import { registerAssetUrls } from "../../../features/digitalTwin/equipment/definition/assetResolver";
import type { AssetInspection, AssetUrlEntry } from "../../../features/digitalTwin/equipment/definition/assetResolver";
import { apiFetch } from "../../../lib/apiFetch";

const BASE = "/api/equipment-catalog-handler";

export type AssetKind = "equipment" | "system";
export type ReviewState = "reviewed" | "needs_review";

export type ClassNode = {
  id: string;
  code: string;
  name: string;
  name_en: string | null;
  asset_kind: AssetKind;
  parent_id: string | null;
  sort_order: number;
  is_locked: boolean;
  is_placeholder: boolean;
  asset_count: number;
  model_count: number;
  children: ClassNode[];
};

export type ModelSummary = {
  /** 트윈 연결(20261008 SQL 뒤에만 온다) */
  twin_spec_id?: string | null;
  show_in_twin?: boolean;
  /** 매입가가 있는 파트너(목록용) */
  partner_ids?: string[];
  id: string;
  asset_id: string;
  kind: "product" | "reference";
  name: string;
  manufacturer: string | null;
  model_no: string | null;
  /** 기술 분류 27코드 · 제품 코드(「제조사-모델번호」) — 20261108 SQL 뒤에만 온다(undefined = 칸 없음) */
  tech_code?: string | null;
  product_code?: string | null;
  width_mm_max: number | null;
  length_mm_max: number | null;
  height_mm_max: number | null;
  capacity_kg: number | null;
  capacity_basis: string | null;
  review_state: ReviewState;
  is_provisional: boolean;
  needs_split: boolean;
  sort_order: number;
};

export type ModelFull = ModelSummary & {
  /** JSON 3D 정의(20261008c SQL 뒤에만 칸이 온다 — undefined = 칸 없음, null = 정의 없음) */
  twin_definition?: StoredDefinition | null;
  twin_params?: Record<string, unknown>;
  /** [포트 2단계] 능력 프로필 모델 덮어쓰기(20261014b 뒤에만 칸이 온다) — ports = 포트 허용 범위 */
  profile_patch?: Record<string, unknown> | null;
  usage: string | null;
  width_mm_min: number | null;
  length_mm_min: number | null;
  height_mm_min: number | null;
  dimension_type: string | null;
  self_weight_kg: number | null;
  throughput: string | null;
  power: string | null;
  specs: Record<string, unknown>;
  note: string | null;
  sku_model_code: string | null;
  raw_row: Record<string, unknown>;
  created_at: string;
  updated_at: string;
};

export type AssetRow = {
  id: string;
  class_id: string;
  asset_kind: AssetKind;
  code: string;
  name: string;
  name_en: string | null;
  manufacturer: string | null;
  description: string | null;
  representative_model_id: string | null;
  review_state: ReviewState;
  is_provisional: boolean;
  provisional_note: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
  spec_id: string | null;
  models: ModelSummary[];
  /** 모델들의 파트너 합(목록용) */
  partner_ids?: string[];
};

export type CatalogStats = {
  assets: number;
  equipment: number;
  system: number;
  models: number;
  product: number;
  reference: number;
  with_representative: number;
  needs_review: number;
  with_3d: number;
  provisional: number;
};

export type Overview = { tree: ClassNode[]; assets: AssetRow[]; stats: CatalogStats; unclassifiedClassId: string | null };

export type Bom = {
  id: string;
  asset_id: string;
  part_code: string;
  part_name: string;
  spec: { sources?: Array<{ equipment_id: string; spec: string | null }>; text?: string | null };
  sort_order: number;
};

export type PartnerPrice = {
  id: string;
  model_id: string;
  partner_id: string;
  partner_name: string | null;
  currency: string;
  supply_price: number | null;
  install_cost: number | null;
  lead_time_days: number | null;
  maintenance_yearly: number | null;
  valid_from: string | null;
  valid_to: string | null;
  note: string | null;
  /** 공급 역할(20261109 뒤에만 온다) */
  roles?: SupplyRole[];
};

export type LoadUnit = {
  id: string;
  kind: "container" | "cargo";
  name: string;
  owner_asset_id?: string | null;
  owner_asset_name?: string | null;
  basis_kind: string | null;
  width_mm: number | null;
  depth_mm: number | null;
  height_mm: number | null;
  weight_kg: number | null;
  capacity_kg: number | null;
  unit: string | null;
  raw_text: string | null;
  issue_code: string | null;
  issue_note: string | null;
  source_equipment_id?: string | null;
  source_model_id?: string | null;
  compat?: Array<{ asset_id: string; asset_name: string | null; confirmed: boolean; origin: string }>;
};

export type AssetDetail = {
  asset: Omit<AssetRow, "spec_id" | "models"> & { path: Array<{ id: string; name: string; code: string }> };
  models: ModelFull[];
  threeD: { asset_id: string; spec_id: string; param_presets: Record<string, unknown>; match_rule: string | null } | null;
  boms: Bom[];
  prices: PartnerPrice[];
  interfaces: Array<{ id: string; direction: string | null; protocol: string | null; format: string | null; standard: string | null; spec_doc_id: string | null }>;
  cargo: LoadUnit[];
  links: Array<{ master_uid: string; equipment_id: string; role: string; model_id: string | null; match_rule: string; linked_at: string; batch_id: string }>;
  /** [STEP 2-B] 모델의 3D 자산(crm_catalog_model_assets — 저장 위치 bucket · 경로는 서버가 빼고 준다). 표가 없으면 빈 목록 */
  model_assets?: ModelAsset[];
  /** [STEP 2-B] 3D 정의가 가리키는 자산의 내려받기 주소(서명 — STEP 2-C) · 검사 결과 보조값 */
  asset_urls?: Record<string, AssetUrlEntry>;
  /** [STEP 2-C] 서명 주소를 허용할 Storage 출처(SUPABASE_URL origin) */
  storage_origin?: string | null;
  /** [STEP 2-C] 업로드 · 검사 기준(서버 GLB_LIMITS — 화면은 숫자를 따로 적지 않는다) */
  asset_limits?: AssetLimits;
};

/** [STEP 2-C] 업로드 · 검사 기준 */
export type AssetLimits = { app_max_bytes: number; triangle_warn: number; triangle_max: number; texture_max: number };

/** [STEP 2-B] 카탈로그 3D 자산 한 개(읽기) — 파일 정보 · 검사 결과의 정본은 DB */
export type ModelAsset = {
  id: string; model_id: string; kind: "glb"; status: "pending" | "ready" | "rejected" | "archived";
  original_filename: string | null; mime_type: string | null; file_size: number | null; sha256: string | null;
  inspection: AssetInspection | null; created_at: string; updated_at: string;
};

export type UnclassifiedRow = { id: string; name: string; asset_kind: AssetKind; description: string | null; recommendation: { class_id: string; class_code: string; reason: string } | null };

export const fetchOverview = () => apiFetch<Overview & { ok: true }>(`${BASE}?resource=overview`);
// [STEP 2-B] 상세 응답의 asset_urls 를 resolver 에 넣는다 — 3D 탭 미리보기가 asset_id 로 GLB 를 그린다
export const fetchAsset = (id: string) => apiFetch<AssetDetail & { ok: true }>(`${BASE}?resource=asset&id=${encodeURIComponent(id)}`).then((r) => { if (r && r.asset_urls) registerAssetUrls(r.asset_urls, r.storage_origin); return r; });
export const fetchLoadUnits = () => apiFetch<{ ok: true; rows: LoadUnit[] }>(`${BASE}?resource=load-units`);
export const fetchUnclassified = () => apiFetch<{ ok: true; unclassifiedClassId: string | null; rows: UnclassifiedRow[] }>(`${BASE}?resource=unclassified`);

export const createClass = (input: { parent_id?: string | null; asset_kind: AssetKind; name: string }) =>
  apiFetch<{ ok: true; class: ClassNode }>(`${BASE}?resource=class`, { method: "POST", json: input });
export const updateClass = (input: { id: string; name?: string; parent_id?: string }) =>
  apiFetch<{ ok: true }>(`${BASE}?resource=class`, { method: "PUT", json: input });
export const deleteClass = (id: string, moveTo?: string | null) =>
  apiFetch<{ ok: true; deleted: number; moved: number }>(`${BASE}?resource=class&id=${encodeURIComponent(id)}${moveTo ? `&move_to=${encodeURIComponent(moveTo)}` : ""}`, { method: "DELETE" });
export const reorderClasses = (parentId: string | null, ids: string[]) =>
  apiFetch<{ ok: true }>(`${BASE}?resource=class-order`, { method: "PUT", json: { parent_id: parentId, ids } });
/** 같은 카테고리 설비 순서 — ids 는 그 카테고리 설비 전부(2026-10-13 QA P3) */
export const reorderAssets = (classId: string, ids: string[]) =>
  apiFetch<{ ok: true }>(`${BASE}?resource=asset-order`, { method: "PUT", json: { class_id: classId, ids } });
/** 같은 설비 모델 순서 — ids 는 그 설비 모델 전부 */
export const reorderModels = (assetId: string, ids: string[]) =>
  apiFetch<{ ok: true }>(`${BASE}?resource=model-order`, { method: "PUT", json: { asset_id: assetId, ids } });

export const createAsset = (input: { class_id: string; name: string; manufacturer?: string; description?: string }) =>
  apiFetch<{ ok: true; asset: AssetRow }>(`${BASE}?resource=asset`, { method: "POST", json: input });
export const moveAssets = (assetIds: string[], classId: string) =>
  apiFetch<{ ok: true; moved: number }>(`${BASE}?resource=asset-move`, { method: "PUT", json: { asset_ids: assetIds, class_id: classId } });
export const setRepresentative = (assetId: string, modelId: string | null) =>
  apiFetch<{ ok: true }>(`${BASE}?resource=representative`, { method: "PUT", json: { asset_id: assetId, model_id: modelId } });
export const createModel = (input: Record<string, string | null | undefined> & { asset_id: string; model_no: string }) =>
  apiFetch<{ ok: true; model: ModelSummary }>(`${BASE}?resource=model`, { method: "POST", json: input });

export const saveBom = (input: { id?: string; asset_id?: string; part_name: string; part_code?: string; spec_text?: string }) =>
  apiFetch<{ ok: true; bom: Bom }>(`${BASE}?resource=bom`, { method: input.id ? "PUT" : "POST", json: input });
export const deleteBom = (id: string) => apiFetch<{ ok: true }>(`${BASE}?resource=bom&id=${encodeURIComponent(id)}`, { method: "DELETE" });

// ── 편집(2026-10-07 2차) ───────────────────────────────────────────────
export const updateAsset = (input: { id: string; name?: string; manufacturer?: string | null; description?: string | null }) =>
  apiFetch<{ ok: true; asset: AssetRow }>(`${BASE}?resource=asset`, { method: "PUT", json: input });
export const deleteAsset = (id: string) => apiFetch<{ ok: true }>(`${BASE}?resource=asset&id=${encodeURIComponent(id)}`, { method: "DELETE" });

/** 모델에서 고칠 수 있는 칸(보낸 칸만 바뀐다). 숫자는 문자열로 보내도 된다("3,048") */
export type ModelPatch = Partial<Record<"width_mm_max" | "length_mm_max" | "height_mm_max" | "self_weight_kg" | "capacity_kg", string | number | null>>
  & Partial<Record<"name" | "manufacturer" | "model_no" | "usage" | "capacity_basis" | "throughput" | "power" | "note" | "twin_spec_id" | "tech_code" | "product_code", string | null>>
  & { show_in_twin?: boolean; twin_params?: Record<string, unknown>; twin_definition?: StoredDefinition | null; autostore_port?: AutostorePortSpec | null;
    /** [포트 2단계 Q1] 모델 능력 프로필 덮어쓰기의 포트 규칙(null = 지움 → 유형 공통값) */ profile_ports?: Record<string, unknown> | null };
/** [PA 2026-10-14] AutoStore 포트 사양(모델 specs.autostore_port) — 트윈이 포트 종류별 표준값으로 쓴다. 처리량 = 최대 */
export type AutostorePortSpec = { type: string; cells: number; drops: number; rate_mezz: number; rate_floor: number; exchange_sec: number; out_depth_m: number; out_only?: boolean; source?: string };
export const updateModel = (id: string, patch: ModelPatch) =>
  apiFetch<{ ok: true; model: ModelFull; changed: number; historySaved: boolean }>(`${BASE}?resource=model`, { method: "PUT", json: { ...patch, id } });
// ── [STEP 2-C] 3D 자산(GLB) 업로드 · 확정 · 되돌리기 · 삭제(admin+) ─────────────
export type AssetUploadUrl = { ok: true; asset_id: string; signed_url: string; expires_in: number; max_bytes: number };
export type AssetResult = { ok: true; asset: ModelAsset; already?: boolean; asset_urls?: Record<string, AssetUrlEntry>; storage_origin?: string | null };
export const createAssetUploadUrl = (input: { model_id: string; filename: string; file_size: number }) =>
  apiFetch<AssetUploadUrl>(`${BASE}?resource=model-asset-upload-url`, { method: "POST", json: input });
/** 서버 응답의 주소를 resolver 에 넣는다(미리보기가 바로 그 asset_id 로 그린다) */
const withUrls = (r: AssetResult) => { if (r && r.asset_urls) registerAssetUrls(r.asset_urls, r.storage_origin); return r; };
export const finalizeAssetUpload = (input: { asset_id: string; model_id: string }) =>
  apiFetch<AssetResult>(`${BASE}?resource=model-asset-finalize`, { method: "POST", json: input }).then(withUrls);
export const restoreModelAsset = (input: { asset_id: string; model_id: string }) =>
  apiFetch<AssetResult>(`${BASE}?resource=model-asset-restore`, { method: "POST", json: input }).then(withUrls);
export const deleteModelAsset = (id: string) => apiFetch<{ ok: true; deleted: string }>(`${BASE}?resource=model-asset&id=${encodeURIComponent(id)}`, { method: "DELETE" });
/**
 * 서명 업로드 주소로 브라우저가 Storage 에 직접 PUT(파일이 우리 서버를 거치지 않는다). 진행률 때문에 XHR(새 의존성 없음).
 * @param onProgress 0–100
 */
export function putToSignedUrl(url: string, file: Blob, onProgress?: (pct: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", "model/gltf-binary");
    xhr.upload.onprogress = (e) => { if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100)); };
    // 실패에 HTTP 상태를 붙인다(화면이 「2/3 Storage 전송(PUT) 실패 (HTTP 403)」 처럼 단계 · 상태를 보인다)
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(Object.assign(new Error(`Storage 응답 ${xhr.status}${xhr.responseText ? ` — ${xhr.responseText.slice(0, 200)}` : ""}`), { status: xhr.status })));
    xhr.onerror = () => reject(new Error("Storage 로 보내는 중 네트워크 오류(CORS · 연결)"));
    xhr.send(file);
  });
}

export const deleteModel = (id: string) => apiFetch<{ ok: true }>(`${BASE}?resource=model&id=${encodeURIComponent(id)}`, { method: "DELETE" });

export type PriceInput = { partner_id?: string; supply_price?: string | number | null; install_cost?: string | number | null; lead_time_days?: string | number | null; maintenance_yearly?: string | number | null; note?: string | null; roles?: SupplyRole[] };
/** review_tech_added = 이 연결로 파트너 기술 분류에 「검토」로 더한 코드 */
export const savePrice = (input: PriceInput & { id?: string; model_id?: string }) =>
  apiFetch<{ ok: true; price: PartnerPrice; historySaved: boolean; review_tech_added?: string | null }>(`${BASE}?resource=price`, { method: input.id ? "PUT" : "POST", json: input });

/** 파트너가 제품을 공급하는 역할(20261109) */
export type SupplyRole = "maker" | "supplier" | "installer";
export const SUPPLY_ROLES: Array<{ key: SupplyRole; ko: string; en: string }> = [
  { key: "maker", ko: "제조사", en: "Maker" },
  { key: "supplier", ko: "공급·총판", en: "Supplier" },
  { key: "installer", ko: "설치·SI", en: "Installer / SI" },
];
/** 파트너 제품 라인업 — 이 파트너가 공급하는 카탈로그 제품 · 역할 · 가격 */
export type PartnerProduct = {
  link: PartnerPrice;
  model: ModelSummary & { usage?: string | null; throughput?: string | null };
  asset: { id: string; code: string; name: string; asset_kind: AssetKind; path: Array<{ id: string; code: string; name: string }> } | null;
};
export const fetchPartnerProducts = (partnerId: string) =>
  apiFetch<{ ok: true; products: PartnerProduct[]; roles_ready: boolean }>(`${BASE}?resource=partner-products&partner_id=${encodeURIComponent(partnerId)}`);

/**
 * 기술 분류(설비 카테고리) 관리 — 「기술 분류」 탭(2026-11-05). 서버 lib/tech-categories-store.js · lib/tech-category-catalog.js.
 *   storage "static" = DB 표(20261115) 전 — 보기만 된다.
 *   aliases = 추출 서버로 나가는 최종 별칭(제품명 · 기본 · 사람이 더한 것) · manual_aliases = 사람이 더한 것(고칠 수 있는 것)
 */
export type TechCategoryRow = {
  code: string; name: string; description: string; aliases: string[]; manual_aliases: string[]; active: boolean;
  group: string; group_label: string; sort_order: number; product_count: number; updated_at: string | null; updated_by: string | null;
};
export type TechCategoriesResponse = {
  storage: "db" | "static"; version: string; changed_at: string | null; groups: Array<{ key: string; label: string }>;
  limits: { aliases: number; alias_len: number; description_len: number }; categories: TechCategoryRow[];
};
export const fetchTechCategories = () => apiFetch<TechCategoriesResponse>(`${BASE}?resource=tech-categories`);
export const createTechCategory = (v: { code: string; group: string; name: string; description?: string; aliases?: string[] }) =>
  apiFetch<{ ok: true }>(`${BASE}?resource=tech-category`, { method: "POST", json: v });
export const updateTechCategory = (code: string, patch: Partial<{ name: string; description: string; aliases: string[]; active: boolean; group: string; sort_order: number }>) =>
  apiFetch<{ ok: true }>(`${BASE}?resource=tech-category`, { method: "PUT", json: { code, ...patch } });
export const refreshTechCategories = () => apiFetch<{ ok: true }>(`${BASE}?resource=tech-categories-refresh`, { method: "POST", json: {} });

/** 기술 코드 조회(2026-11-03 · RFQ) — 코드 → 설비 · 제품 · 공급 파트너 + 후보 파트너. lib/equipment-catalog.js techLookup 과 같은 모양 */
export type TechLookupSupplier = {
  partner_id: string; partner_name: string; roles: SupplyRole[]; currency: string | null;
  supply_price: number | null; install_cost: number | null; lead_time_days: number | null; maintenance_yearly: number | null;
};
export type TechLookupProduct = {
  id: string; name: string; manufacturer: string | null; model_no: string | null; product_code: string | null;
  tech_code: string; review_state: string | null; suppliers: TechLookupSupplier[];
};
export type TechLookupEquipment = {
  id: string; code: string; name: string; asset_kind: AssetKind; path: Array<{ id: string; code: string; name: string }>;
  tech_codes: string[]; products: TechLookupProduct[];
};
/** status — supplier = 그 코드 제품에 공급 연결됨 · candidate = 기술 분류만(공급 가능 여부 확인 전). review = 아직 「검토」 기술 */
export type TechLookupPartner = {
  id: string; name: string; name_en: string | null; progress: string | null; manager_name: string | null; technologies: string[];
  codes: Array<{ code: string; status: "supplier" | "candidate"; review: boolean }>;
  kind: "supplier" | "candidate";
  supplies: Array<{ model_id: string; asset_id: string; product_code: string | null; product_name: string; tech_code: string; roles: SupplyRole[] }>;
};
export type TechLookup = {
  codes: Array<{ code: string; name: string; equipment_count: number; product_count: number; supplier_count: number; candidate_count: number }>;
  equipment: TechLookupEquipment[];
  partners: TechLookupPartner[];
  roles_ready: boolean;
};
export const fetchTechLookup = (codes: string[]) =>
  apiFetch<{ ok: true } & TechLookup>(`${BASE}?resource=tech-lookup&codes=${encodeURIComponent(codes.join(","))}`);
export const deletePrice = (id: string) => apiFetch<{ ok: true }>(`${BASE}?resource=price&id=${encodeURIComponent(id)}`, { method: "DELETE" });

export type HistoryRow = {
  id: string;
  asset_id: string | null;
  model_id: string | null;
  price_id: string | null;
  partner_id: string | null;
  kind: "model" | "price";
  action: "create" | "update" | "delete";
  field: string | null;
  old_value: string | null;
  new_value: string | null;
  actor_email: string | null;
  actor_name: string | null;
  created_at: string;
};
// ── 일괄 가져오기 · 내보내기 ─────────────────────────────────────────────
export type ImportResult = {
  ok: boolean; dry_run: boolean; errors?: string[]; warnings?: string[];
  counts?: Record<"assetCreate" | "assetUpdate" | "modelCreate" | "modelUpdate" | "modelSame" | "priceCreate" | "priceUpdate", number>;
  assets?: Array<{ label: string; action: "create" | "update" | "same"; models: Array<{ label: string; action: "create" | "update" | "same"; changes: number; prices: number }> }>;
  done?: { assets: number; models: number; prices: number; history: number };
  error?: string;
  /** 미리보기 — 적용 함수(DB)가 있는가. false 면 적용을 막는다(2026-10-12 P1) */
  applyReady?: boolean;
  /** 적용 함수가 DB 에 없음 — 관리자가 sqlFile 을 실행해야 한다 */
  rpcMissing?: boolean;
  sqlFile?: string;
};
export const exportCatalog = (assetIds?: string[]) => apiFetch<{ ok: true; assets: any[] }>(`${BASE}?resource=export${assetIds && assetIds.length ? `&asset_ids=${encodeURIComponent(assetIds.join(","))}` : ""}`);
export const importCatalog = (payload: { assets: unknown[] }, dryRun: boolean) =>
  apiFetch<ImportResult>(`${BASE}?resource=import`, { method: "POST", json: { ...payload, dry_run: dryRun } });

export const fetchHistory = (q: { modelId?: string; assetId?: string }) =>
  apiFetch<{ ok: true; rows: HistoryRow[]; tableMissing?: boolean }>(`${BASE}?resource=history&${q.modelId ? `model_id=${encodeURIComponent(q.modelId)}` : `asset_id=${encodeURIComponent(q.assetId || "")}`}`);

// ── 표시 도우미 ─────────────────────────────────────────────────────────
const fmt = (n: number | null | undefined) => (n == null ? null : Number(n).toLocaleString("ko-KR", { maximumFractionDigits: 2 }));

/** "W × D × H" (mm). 하나도 없으면 null */
export function dimsText(m: { width_mm_max: number | null; length_mm_max: number | null; height_mm_max: number | null }): string | null {
  const parts = [m.width_mm_max, m.length_mm_max, m.height_mm_max];
  if (parts.every((p) => p == null)) return null;
  return parts.map((p) => fmt(p) ?? "—").join(" × ");
}

export function numText(n: number | null | undefined, unit = ""): string | null {
  const s = fmt(n);
  return s == null ? null : unit ? `${s} ${unit}` : s;
}

/** 분류 트리 → id 로 찾기 · 경로 */
export function flattenTree(tree: ClassNode[]): Map<string, { node: ClassNode; major: ClassNode }> {
  const out = new Map<string, { node: ClassNode; major: ClassNode }>();
  for (const major of tree) {
    out.set(major.id, { node: major, major });
    for (const c of major.children) out.set(c.id, { node: c, major });
  }
  return out;
}
